import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  FormControlLabel,
  Switch,
  CircularProgress,
  IconButton,
  Typography,
  Divider,
  Alert,
  Autocomplete,
  useTheme,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import {
  Close as CloseIcon,
  Refresh as RefreshIcon,
  Business as BranchIcon,
  Language as LanguageIcon,
  Code as CodeIcon,
  Phone as PhoneIcon,
  Home as HomeIcon,
  Person as PersonIcon,
  Star as StarIcon,
  AccountBalance as AccountBalanceIcon,
  TrendingUp as RevenueIcon,
  TrendingDown as ExpenseIcon,
  Inventory as InventoryIcon
} from '@mui/icons-material';
import { useLanguage } from '../../contexts/LanguageContext';
import { Branch, CreateBranchDto, UpdateBranchDto } from '../../types/branches.types';
import { branchesService } from '../../services/branches.service';
import { chartOfAccountsService } from '../../services/chartOfAccounts.service';
import CostCenterSelector from '../common/CostCenterSelector';

interface BranchDialogProps {
  open: boolean;
  onClose: () => void;
  branch?: Branch;
  onSave: () => void;
}

interface Account {
  accountId: number;
  accountCode: string;
  accountName: string;
  accountNameAr: string;
  accountType: string;
}

const BranchDialog: React.FC<BranchDialogProps> = ({ open, onClose, branch, onSave }) => {
  const { language } = useLanguage();
  const isRTL = language === 'ar';
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  const t = {
    addBranch: isRTL ? 'إضافة فرع جديد' : 'Add New Branch',
    editBranch: isRTL ? 'تعديل فرع' : 'Edit Branch',
    basicInfo: isRTL ? 'المعلومات الأساسية' : 'Basic Information',
    contactInfo: isRTL ? 'معلومات الاتصال' : 'Contact Information',
    accountingInfo: isRTL ? 'المعلومات المحاسبية' : 'Accounting Information',
    branchCode: isRTL ? 'كود الفرع' : 'Branch Code',
    branchName: isRTL ? 'اسم الفرع (EN)' : 'Branch Name (EN)',
    branchNameAr: isRTL ? 'اسم الفرع (AR)' : 'Branch Name (AR)',
    phone: isRTL ? 'الهاتف' : 'Phone',
    address: isRTL ? 'العنوان' : 'Address',
    managerId: isRTL ? 'رقم المدير' : 'Manager ID',
    isMainBranch: isRTL ? 'فرع رئيسي' : 'Main Branch',
    isActive: isRTL ? 'نشط' : 'Active',
    branchType: isRTL ? 'نوع الفرع' : 'Branch Type',
    branchTypeOperational: isRTL ? 'فرع تشغيلي' : 'Operational Branch',
    branchTypeWarehouse: isRTL ? 'فرع مخزن مركزي' : 'Warehouse Branch',
    branchTypeVirtual: isRTL ? 'فرع افتراضي' : 'Virtual Branch',
    branchTypeHint: isRTL
      ? 'فرع المخزن لا يظهر في التحويلات — يبيع للفروع فقط'
      : 'Warehouse branch is excluded from transfers — sells to branches only',
    inventoryAccount: isRTL ? 'حساب مخزون الفرع' : 'Branch Inventory Account',
    revenueAccount: isRTL ? 'حساب الإيرادات' : 'Revenue Account',
    expenseAccount: isRTL ? 'حساب المصاريف' : 'Expense Account',
    costCenter: isRTL ? 'مركز التكلفة' : 'Cost Center',
    selectInventoryAccount: isRTL ? 'اختر حساب المخزون' : 'Select Inventory Account',
    selectRevenueAccount: isRTL ? 'اختر حساب الإيرادات' : 'Select Revenue Account',
    selectExpenseAccount: isRTL ? 'اختر حساب المصاريف' : 'Select Expense Account',
    selectCostCenter: isRTL ? 'اختر مركز التكلفة' : 'Select Cost Center',
    save: isRTL ? 'حفظ' : 'Save',
    cancel: isRTL ? 'إلغاء' : 'Cancel',
    optional: isRTL ? 'اختياري' : 'Optional',
    required: isRTL ? 'مطلوب' : 'Required',
    loadingAccounts: isRTL ? 'جاري تحميل الحسابات...' : 'Loading accounts...',
    noAccountsFound: isRTL ? 'لا توجد حسابات نهائية' : 'No final accounts found',
    inventoryAccountRequired: isRTL ? 'حساب المخزون مطلوب لاستقبال البضائع' : 'Inventory account required to receive goods',
  };

  const [formData, setFormData] = useState({
    branchCode: '',
    branchName: '',
    branchNameAr: '',
    address: '',
    phone: '',
    managerId: '',
    isMainBranch: false,
    branchType: 'Operational',
    inventoryAccountId: null as number | null,
    revenueAccountId: null as number | null,
    expenseAccountId: null as number | null,
    costCenterId: null as number | null,
    isActive: true,
  });

  const [accounts, setAccounts] = useState<Account[]>([]);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (open) {
      loadAccounts();
      if (branch) {
        setFormData({
          branchCode: branch.branchCode,
          branchName: branch.branchName,
          branchNameAr: branch.branchNameAr,
          address: branch.address || '',
          phone: branch.phone || '',
          managerId: branch.managerId?.toString() || '',
          isMainBranch: branch.isMainBranch || false,
          branchType: branch.branchType || 'Operational',
          inventoryAccountId: branch.inventoryAccountId || null,
          revenueAccountId: branch.revenueAccountId || null,
          expenseAccountId: branch.expenseAccountId || null,
          costCenterId: branch.costCenterId || null,
          isActive: branch.isActive ?? true,
        });
      } else {
        loadNewBranchCode();
      }
    }
  }, [open, branch]);

  const loadAccounts = async () => {
    setLoadingAccounts(true);
    try {
      const data = await chartOfAccountsService.getAll();
      const finalAccounts = data.filter((acc: any) =>
        acc.isFinalAccount && acc.isActive
      );
      setAccounts(finalAccounts);
    } catch (error) {
      console.error('Failed to load accounts:', error);
    } finally {
      setLoadingAccounts(false);
    }
  };

  const loadNewBranchCode = async () => {
    try {
      const code = await branchesService.generateCode();
      setFormData(prev => ({ ...prev, branchCode: code }));
    } catch (error) {
      console.error('Failed to generate branch code:', error);
    }
  };

  const handleGenerateCode = () => {
    loadNewBranchCode();
  };

  const handleChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!formData.branchName.trim()) {
      newErrors.branchName = isRTL ? 'اسم الفرع (إنجليزي) مطلوب' : 'Branch name (EN) is required';
    }
    if (!formData.branchNameAr.trim()) {
      newErrors.branchNameAr = isRTL ? 'اسم الفرع (عربي) مطلوب' : 'Branch name (AR) is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setLoading(true);
    try {
      if (branch) {
        const updateDto: UpdateBranchDto = {
          branchId: branch.branchId,
          branchName: formData.branchName,
          branchNameAr: formData.branchNameAr,
          address: formData.address || undefined,
          phone: formData.phone || undefined,
          managerId: formData.managerId ? parseInt(formData.managerId) : undefined,
          isMainBranch: formData.isMainBranch,
          branchType: formData.branchType,
          inventoryAccountId: formData.inventoryAccountId ?? undefined,
          revenueAccountId: formData.revenueAccountId ?? undefined,
          expenseAccountId: formData.expenseAccountId ?? undefined,
          costCenterId: formData.costCenterId ?? undefined,
          isActive: formData.isActive,
        };
        await branchesService.update(branch.branchId, updateDto);
      } else {
        const createDto: CreateBranchDto = {
          branchCode: formData.branchCode,
          branchName: formData.branchName,
          branchNameAr: formData.branchNameAr,
          address: formData.address || undefined,
          phone: formData.phone || undefined,
          managerId: formData.managerId ? parseInt(formData.managerId) : undefined,
          isMainBranch: formData.isMainBranch,
          branchType: formData.branchType,
          inventoryAccountId: formData.inventoryAccountId ?? undefined,
          revenueAccountId: formData.revenueAccountId ?? undefined,
          expenseAccountId: formData.expenseAccountId ?? undefined,
          costCenterId: formData.costCenterId ?? undefined,
        };
        await branchesService.create(createDto);
      }
      onSave();
      handleClose();
    } catch (error: any) {
      console.error('Failed to save branch:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      branchCode: '',
      branchName: '',
      branchNameAr: '',
      address: '',
      phone: '',
      managerId: '',
      isMainBranch: false,
      branchType: 'Operational',
      inventoryAccountId: null,
      revenueAccountId: null,
      expenseAccountId: null,
      costCenterId: null,
      isActive: true,
    });
    setErrors({});
    onClose();
  };

  const selectedInventoryAccount = accounts.find(acc => acc.accountId === formData.inventoryAccountId);
  const selectedRevenueAccount = accounts.find(acc => acc.accountId === formData.revenueAccountId);
  const selectedExpenseAccount = accounts.find(acc => acc.accountId === formData.expenseAccountId);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      dir={isRTL ? 'rtl' : 'ltr'}
      PaperProps={{ sx: { borderRadius: 3, bgcolor: isDarkMode ? '#1a1a2e' : 'white' } }}
    >
      <DialogTitle sx={{ background: isDarkMode ? 'linear-gradient(135deg, #0f3460 0%, #16213e 100%)' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <BranchIcon />
          <Typography variant="h6" fontWeight="bold">{branch ? t.editBranch : t.addBranch}</Typography>
        </Box>
        <IconButton onClick={handleClose} sx={{ color: 'white' }}><CloseIcon /></IconButton>
      </DialogTitle>

      <DialogContent sx={{ mt: 2 }}>
        {/* Basic Information */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <BranchIcon sx={{ color: '#667eea' }} />
            <Typography variant="h6" fontWeight="bold" sx={{ color: '#667eea' }}>{t.basicInfo}</Typography>
          </Box>
          <Divider sx={{ mb: 2 }} />

          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField fullWidth label={t.branchCode} value={formData.branchCode} onChange={handleChange('branchCode')} disabled={!!branch} error={!!errors.branchCode} helperText={errors.branchCode} InputProps={{ startAdornment: <CodeIcon sx={{ mr: 1, color: 'action.active' }} /> }} />
              {!branch && <IconButton color="primary" onClick={handleGenerateCode} sx={{ bgcolor: 'rgba(102, 126, 234, 0.1)' }}><RefreshIcon /></IconButton>}
            </Box>

            <TextField fullWidth label={`${t.managerId} (${t.optional})`} type="number" value={formData.managerId} onChange={handleChange('managerId')} error={!!errors.managerId} helperText={errors.managerId} InputProps={{ startAdornment: <PersonIcon sx={{ mr: 1, color: 'action.active' }} /> }} />

            <TextField fullWidth label={`${t.branchName} *`} value={formData.branchName} onChange={handleChange('branchName')} error={!!errors.branchName} helperText={errors.branchName} required InputProps={{ startAdornment: <LanguageIcon sx={{ mr: 1, color: 'action.active' }} /> }} />

            <TextField fullWidth label={`${t.branchNameAr} *`} value={formData.branchNameAr} onChange={handleChange('branchNameAr')} error={!!errors.branchNameAr} helperText={errors.branchNameAr} required InputProps={{ startAdornment: <LanguageIcon sx={{ mr: 1, color: 'action.active' }} /> }} />

            <Box sx={{ gridColumn: 'span 2' }}>
              <FormControlLabel control={<Switch checked={formData.isMainBranch} onChange={handleChange('isMainBranch')} color="warning" />} label={<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><StarIcon color="warning" /><Typography fontWeight="bold">{t.isMainBranch}</Typography></Box>} />
            </Box>

            <Box sx={{ gridColumn: 'span 2' }}>
              <FormControl fullWidth>
                <InputLabel>{t.branchType}</InputLabel>
                <Select
                  value={formData.branchType}
                  label={t.branchType}
                  onChange={(e) => setFormData(prev => ({ ...prev, branchType: e.target.value }))}
                >
                  <MenuItem value="Operational">{t.branchTypeOperational}</MenuItem>
                  <MenuItem value="Warehouse">{t.branchTypeWarehouse}</MenuItem>
                  <MenuItem value="Virtual">{t.branchTypeVirtual}</MenuItem>
                </Select>
              </FormControl>
              {formData.branchType === 'Warehouse' && (
                <Alert severity="info" sx={{ mt: 1 }}>{t.branchTypeHint}</Alert>
              )}
            </Box>
          </Box>
        </Box>

        {/* Contact Information */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <PhoneIcon sx={{ color: '#667eea' }} />
            <Typography variant="h6" fontWeight="bold" sx={{ color: '#667eea' }}>{t.contactInfo}</Typography>
          </Box>
          <Divider sx={{ mb: 2 }} />

          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
            <TextField fullWidth label={`${t.phone} (${t.optional})`} value={formData.phone} onChange={handleChange('phone')} error={!!errors.phone} helperText={errors.phone} InputProps={{ startAdornment: <PhoneIcon sx={{ mr: 1, color: 'action.active' }} /> }} />
            <Box></Box>
            <Box sx={{ gridColumn: 'span 2' }}>
              <TextField fullWidth label={`${t.address} (${t.optional})`} value={formData.address} onChange={handleChange('address')} multiline rows={2} error={!!errors.address} helperText={errors.address} InputProps={{ startAdornment: <HomeIcon sx={{ mr: 1, mt: 1, color: 'action.active' }} /> }} />
            </Box>
          </Box>
        </Box>

        {/* Accounting Information */}
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <AccountBalanceIcon sx={{ color: '#667eea' }} />
            <Typography variant="h6" fontWeight="bold" sx={{ color: '#667eea' }}>{t.accountingInfo}</Typography>
          </Box>
          <Divider sx={{ mb: 2 }} />

          <Box sx={{ display: 'grid', gap: 2 }}>
            {/* Inventory Account - CRITICAL */}
            <Autocomplete fullWidth options={accounts} getOptionLabel={(option) => `${option.accountCode} - ${isRTL ? option.accountNameAr : option.accountName} (${option.accountType})`} value={selectedInventoryAccount || null} onChange={(_, newValue) => { setFormData({ ...formData, inventoryAccountId: newValue?.accountId || null }); }} loading={loadingAccounts} renderInput={(params) => <TextField {...params} label={`${t.inventoryAccount} (${t.required})`} placeholder={t.selectInventoryAccount} InputProps={{ ...params.InputProps, startAdornment: (<><InventoryIcon sx={{ ml: 1, mr: 1, color: 'warning.main' }} />{params.InputProps.startAdornment}</>), endAdornment: (<>{loadingAccounts ? <CircularProgress color="inherit" size={20} /> : null}{params.InputProps.endAdornment}</>), }} />} renderOption={(props, option) => { const { key, ...otherProps } = props; return (<li key={key} {...otherProps}><Box><Typography variant="body2" fontWeight="bold">{option.accountCode}</Typography><Typography variant="caption" color="text.secondary">{isRTL ? option.accountNameAr : option.accountName} • {option.accountType}</Typography></Box></li>); }} noOptionsText={t.noAccountsFound} />

            {/* Revenue Account */}
            <Autocomplete fullWidth options={accounts} getOptionLabel={(option) => `${option.accountCode} - ${isRTL ? option.accountNameAr : option.accountName} (${option.accountType})`} value={selectedRevenueAccount || null} onChange={(_, newValue) => { setFormData({ ...formData, revenueAccountId: newValue?.accountId || null }); }} loading={loadingAccounts} renderInput={(params) => <TextField {...params} label={`${t.revenueAccount} (${t.optional})`} placeholder={t.selectRevenueAccount} InputProps={{ ...params.InputProps, startAdornment: (<><RevenueIcon sx={{ ml: 1, mr: 1, color: 'success.main' }} />{params.InputProps.startAdornment}</>), endAdornment: (<>{loadingAccounts ? <CircularProgress color="inherit" size={20} /> : null}{params.InputProps.endAdornment}</>), }} />} renderOption={(props, option) => { const { key, ...otherProps } = props; return (<li key={key} {...otherProps}><Box><Typography variant="body2" fontWeight="bold">{option.accountCode}</Typography><Typography variant="caption" color="text.secondary">{isRTL ? option.accountNameAr : option.accountName} • {option.accountType}</Typography></Box></li>); }} noOptionsText={t.noAccountsFound} />

            {/* Expense Account */}
            <Autocomplete fullWidth options={accounts} getOptionLabel={(option) => `${option.accountCode} - ${isRTL ? option.accountNameAr : option.accountName} (${option.accountType})`} value={selectedExpenseAccount || null} onChange={(_, newValue) => { setFormData({ ...formData, expenseAccountId: newValue?.accountId || null }); }} loading={loadingAccounts} renderInput={(params) => <TextField {...params} label={`${t.expenseAccount} (${t.optional})`} placeholder={t.selectExpenseAccount} InputProps={{ ...params.InputProps, startAdornment: (<><ExpenseIcon sx={{ ml: 1, mr: 1, color: 'error.main' }} />{params.InputProps.startAdornment}</>), endAdornment: (<>{loadingAccounts ? <CircularProgress color="inherit" size={20} /> : null}{params.InputProps.endAdornment}</>), }} />} renderOption={(props, option) => { const { key, ...otherProps } = props; return (<li key={key} {...otherProps}><Box><Typography variant="body2" fontWeight="bold">{option.accountCode}</Typography><Typography variant="caption" color="text.secondary">{isRTL ? option.accountNameAr : option.accountName} • {option.accountType}</Typography></Box></li>); }} noOptionsText={t.noAccountsFound} />

            {/* Cost Center - NEW */}
            <CostCenterSelector
              value={formData.costCenterId}
              onChange={(id) => setFormData({ ...formData, costCenterId: id })}
              label={`${t.costCenter} (${t.optional})`}
              fullWidth
            />

            {!formData.inventoryAccountId && (
              <Alert severity="warning">
                {t.inventoryAccountRequired}
              </Alert>
            )}

            {(!formData.revenueAccountId || !formData.expenseAccountId) && (
              <Alert severity="info">
                {isRTL
                  ? 'ربط الفرع بحسابات الإيرادات والمصاريف يساعد في تتبع الأداء المالي لكل فرع بدقة'
                  : 'Linking branch to revenue and expense accounts helps track each branch financial performance accurately'
                }
              </Alert>
            )}
          </Box>
        </Box>

        {/* Active Status */}
        {branch && (
          <Box sx={{ mt: 3, p: 2, bgcolor: isDarkMode ? 'rgba(255,255,255,0.05)' : '#f5f5f5', borderRadius: 2 }}>
            <FormControlLabel control={<Switch checked={formData.isActive} onChange={handleChange('isActive')} color="success" />} label={<Typography variant="body1" fontWeight="bold">{t.isActive}</Typography>} />
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 3, bgcolor: isDarkMode ? 'rgba(255,255,255,0.05)' : '#f5f5f5' }}>
        <Button onClick={handleClose} disabled={loading} size="large">{t.cancel}</Button>
        <Button onClick={handleSubmit} variant="contained" disabled={loading} startIcon={loading ? <CircularProgress size={20} /> : null} size="large" sx={{ background: 'linear-gradient(45deg, #667eea 30%, #764ba2 90%)', boxShadow: '0 3px 5px 2px rgba(102, 126, 234, .3)', '&:hover': { background: 'linear-gradient(45deg, #5568d3 30%, #653d8a 90%)' } }}>{t.save}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default BranchDialog;
